/**
 * WakeLockBadge — indicador visual do estado da Screen Wake Lock API.
 *
 * Usado no `MobileMicPage` para comunicar ao médico se a tela vai
 * permanecer acordada durante a sessão de gravação (importante porque
 * muitos plantonistas deixam o celular na bancada e não querem que ele
 * apague no meio do exame).
 *
 * Regras:
 *   - `active && !failed`  → badge verde "🔓 Tela acordada · não vai dormir"
 *   - `failed`             → badge amarelo "💤 Tela pode dormir — trave manualmente"
 *   - nenhum               → não renderiza (null)
 *
 * Estilo idêntico ao `BatteryIndicator`: Plus Jakarta Sans 8px,
 * border 1px colorido, padding compacto. Pensado pra viver no header.
 */
import React from 'react';

interface WakeLockBadgeProps {
  /** Wake lock atualmente ativo (foi concedido pelo navegador). */
  readonly active: boolean;
  /** Última tentativa de obter wake lock falhou (sem suporte ou negado). */
  readonly failed: boolean;
  /** Função de tradução i18n. */
  readonly t: (key: string, vars?: Record<string, string | number>) => string;
}

export function WakeLockBadge({ active, failed, t }: WakeLockBadgeProps) {
  // Decide qual variante (se alguma) renderizar.
  // Prioridade: active > failed > nada.
  let variant: 'active' | 'failed' | null = null;
  if (active && !failed) {
    variant = 'active';
  } else if (failed) {
    variant = 'failed';
  }

  if (!variant) return null;

  const isActive = variant === 'active';
  const color = isActive ? 'var(--violet)' : '#FFC107';
  const label = isActive ? t('mobile.wakelock.active') : t('mobile.wakelock.failed');

  return (
    <div
      role="status"
      aria-live="polite"
      aria-label={label}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        fontFamily: 'var(--font-sans)',
        fontSize: '12px',
        lineHeight: 1.4,
        color,
        border: `1px solid ${color}`,
        background: 'rgba(34, 37, 50, 0.7)',
        padding: '4px 8px',
        borderRadius: '2px',
        letterSpacing: '0.02em',
        whiteSpace: 'nowrap',
        pointerEvents: 'none',
      }}
    >
      {label}
    </div>
  );
}

export default WakeLockBadge;
